/**
 * Skip Logic Engine for Form Fields and Sections
 * Evaluates skip logic rules and determines field/section visibility
 */

export class SkipLogicEngine {
  constructor(formData, formValues) {
    this.formData = formData;
    this.formValues = formValues;
  }

  /**
   * Evaluates a single skip logic rule
   * @param {Object} rule - The skip logic rule
   * @param {string} fieldValue - The current value of the source field
   * @returns {boolean} - Whether the condition is met
   */
  evaluateCondition(rule, fieldValue) {
    const { condition, value } = rule;
    
    switch (condition) {
      case 'equals':
        return fieldValue === value;
      case 'not_equals':
        return fieldValue !== value;
      case 'contains':
        return fieldValue && fieldValue.toString().toLowerCase().includes(value.toLowerCase());
      default:
        return false;
    }
  }

  /**
   * Determines if a field should be visible based on skip logic rules
   * @param {string} fieldId - The ID of the field to check
   * @returns {boolean} - Whether the field should be visible
   */
  shouldShowField(fieldId) {
    // Find all fields that have skip logic affecting this field
    const affectingFields = this.getAllFields().filter(field => 
      field.skipLogic && field.skipLogic.some(rule => 
        rule.targetType === 'field' && rule.target === fieldId
      )
    );

    if (affectingFields.length === 0) {
      return true; // No skip logic affects this field, so show it
    }

    let shouldShow = true;

    for (const sourceField of affectingFields) {
      const sourceValue = this.formValues[sourceField.id];
      
      for (const rule of sourceField.skipLogic) {
        if (rule.targetType === 'field' && rule.target === fieldId) {
          const conditionMet = this.evaluateCondition(rule, sourceValue);
          
          if (conditionMet) {
            if (rule.action === 'hide') {
              shouldShow = false;
            } else if (rule.action === 'show') {
              shouldShow = true;
            }
          }
        }
      }
    }

    return shouldShow;
  }

  /**
   * Determines if a section should be visible based on skip logic rules
   * @param {string} sectionId - The ID of the section to check
   * @returns {boolean} - Whether the section should be visible
   */
  shouldShowSection(sectionId) {
    // Find all fields that have skip logic affecting this section
    const affectingFields = this.getAllFields().filter(field => 
      field.skipLogic && field.skipLogic.some(rule => 
        rule.targetType === 'section' && rule.target === sectionId
      )
    );

    if (affectingFields.length === 0) {
      return true; // No skip logic affects this section, so show it
    }

    let shouldShow = true;

    for (const sourceField of affectingFields) {
      const sourceValue = this.formValues[sourceField.id];
      
      for (const rule of sourceField.skipLogic) {
        if (rule.targetType === 'section' && rule.target === sectionId) {
          const conditionMet = this.evaluateCondition(rule, sourceValue);
          
          if (conditionMet) {
            if (rule.action === 'hide') {
              shouldShow = false;
            } else if (rule.action === 'show') {
              shouldShow = true;
            }
          }
        }
      }
    }

    return shouldShow;
  }

  /**
   * Gets all fields from all sections
   * @returns {Array} - All fields in the form
   */
  getAllFields() {
    if (!this.formData || !this.formData.sections) {
      return [];
    }
    
    return this.formData.sections.reduce((allFields, section) => {
      return allFields.concat(section.fields || []);
    }, []);
  }

  /**
   * Gets visible sections based on skip logic
   * @returns {Array} - Array of visible sections
   */
  getVisibleSections() {
    if (!this.formData || !this.formData.sections) {
      return [];
    }

    return this.formData.sections.filter(section => 
      this.shouldShowSection(section.id)
    );
  }

  /**
   * Gets visible fields for a specific section
   * @param {string} sectionId - The section ID
   * @returns {Array} - Array of visible fields in the section
   */
  getVisibleFieldsForSection(sectionId) {
    const section = this.formData.sections?.find(s => s.id === sectionId);
    if (!section || !section.fields) {
      return [];
    }

    return section.fields.filter(field => 
      this.shouldShowField(field.id)
    );
  }

  /**
   * Checks if navigation to a specific section should be allowed
   * This considers if the section has any visible fields
   * @param {string} sectionId - The section ID
   * @returns {boolean} - Whether navigation to this section is allowed
   */
  canNavigateToSection(sectionId) {
    if (!this.shouldShowSection(sectionId)) {
      return false;
    }

    const visibleFields = this.getVisibleFieldsForSection(sectionId);
    return visibleFields.length > 0;
  }

  /**
   * Gets the next available section index for navigation
   * @param {number} currentIndex - Current section index
   * @returns {number} - Next available section index, or -1 if none
   */
  getNextAvailableSectionIndex(currentIndex) {
    const sections = this.formData.sections || [];
    
    for (let i = currentIndex + 1; i < sections.length; i++) {
      if (this.canNavigateToSection(sections[i].id)) {
        return i;
      }
    }
    
    return -1;
  }

  /**
   * Gets the previous available section index for navigation
   * @param {number} currentIndex - Current section index
   * @returns {number} - Previous available section index, or -1 if none
   */
  getPreviousAvailableSectionIndex(currentIndex) {
    const sections = this.formData.sections || [];
    
    for (let i = currentIndex - 1; i >= 0; i--) {
      if (this.canNavigateToSection(sections[i].id)) {
        return i;
      }
    }
    
    return -1;
  }

  /**
   * Validates skip logic configuration for potential issues
   * @returns {Array} - Array of validation warnings/errors
   */
  validateSkipLogic() {
    const warnings = [];
    const allFields = this.getAllFields();
    
    allFields.forEach(field => {
      if (field.skipLogic) {
        field.skipLogic.forEach((rule, ruleIndex) => {
          // Check if target exists
          if (rule.targetType === 'field') {
            const targetExists = allFields.some(f => f.id === rule.target);
            if (!targetExists) {
              warnings.push({
                type: 'error',
                field: field.id,
                rule: ruleIndex,
                message: `Target field '${rule.target}' not found`
              });
            }
          } else if (rule.targetType === 'section') {
            const targetExists = this.formData.sections?.some(s => s.id === rule.target);
            if (!targetExists) {
              warnings.push({
                type: 'error',
                field: field.id,
                rule: ruleIndex,
                message: `Target section '${rule.target}' not found`
              });
            }
          }
          
          // Check if value exists in options
          if (field.options && !field.options.includes(rule.value)) {
            warnings.push({
              type: 'warning',
              field: field.id,
              rule: ruleIndex,
              message: `Value '${rule.value}' not found in field options`
            });
          }
          
          // Check for self-referencing rules
          if (rule.targetType === 'field' && rule.target === field.id) {
            warnings.push({
              type: 'error',
              field: field.id,
              rule: ruleIndex,
              message: 'Field cannot target itself'
            });
          }
        });
      }
    });
    
    return warnings;
  }

  /**
   * Gets a summary of all skip logic rules in the form
   * @returns {Object} - Summary statistics
   */
  getSkipLogicSummary() {
    const allFields = this.getAllFields();
    const fieldsWithSkipLogic = allFields.filter(field => field.skipLogic && field.skipLogic.length > 0);
    
    let totalRules = 0;
    let fieldTargets = 0;
    let sectionTargets = 0;
    
    fieldsWithSkipLogic.forEach(field => {
      totalRules += field.skipLogic.length;
      field.skipLogic.forEach(rule => {
        if (rule.targetType === 'field') fieldTargets++;
        if (rule.targetType === 'section') sectionTargets++;
      });
    });
    
    return {
      totalFields: allFields.length,
      fieldsWithSkipLogic: fieldsWithSkipLogic.length,
      totalRules,
      fieldTargets,
      sectionTargets,
      hasSkipLogic: totalRules > 0
    };
  }
}

export default SkipLogicEngine;
